import React from "react";
import { Shield, ShieldCheck, ShieldX } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function InsuranceEnrichmentPanel({ enrichment }) {
  const { t } = useTranslation("components");
  const e = enrichment || {};
  return (
    <section
      className="bg-white border border-zinc-200 rounded-xl p-6"
      data-testid="insurance-enrichment-panel"
    >
      <div className="flex items-center gap-2 mb-5">
        <div className="w-7 h-7 rounded-md grid place-items-center bg-indigo-50 text-indigo-700">
          <Shield size={14} strokeWidth={1.75} />
        </div>
        <h2 className="font-display font-semibold text-base tracking-tight">{t("enrichment.insurance.section_title")}</h2>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">{t("enrichment.insurance.insurer")}</p>
          <p className="font-display font-semibold text-zinc-950" data-testid="ins-enrich-insurer">{e.insurer || "—"}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">{t("enrichment.insurance.policy_number")}</p>
          <p className="font-display font-semibold text-zinc-950" data-testid="ins-enrich-policy-number">{e.policy_number || "—"}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">{t("enrichment.insurance.policy_type")}</p>
          <p className="font-display font-semibold text-zinc-950" data-testid="ins-enrich-policy-type">{e.policy_type || "—"}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="px-4 py-3 rounded-lg border border-emerald-200 bg-emerald-50/40" data-testid="ins-enrich-coverage-summary">
          <div className="flex items-center gap-1.5 mb-1">
            <ShieldCheck size={13} className="text-emerald-700" />
            <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">{t("enrichment.insurance.coverage_summary")}</p>
          </div>
          <p className="text-sm text-zinc-800 leading-relaxed">{e.coverage_summary || "—"}</p>
        </div>
        <div className="px-4 py-3 rounded-lg border border-rose-200 bg-rose-50/40" data-testid="ins-enrich-exclusion-summary">
          <div className="flex items-center gap-1.5 mb-1">
            <ShieldX size={13} className="text-rose-700" />
            <p className="text-[11px] uppercase tracking-wider text-rose-700 font-semibold">{t("enrichment.insurance.exclusion_summary")}</p>
          </div>
          <p className="text-sm text-zinc-800 leading-relaxed">{e.exclusion_summary || "—"}</p>
        </div>
      </div>

      {e.coverage_limits?.length > 0 && (
        <div className="mb-5" data-testid="ins-enrich-coverage-limits">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">{t("enrichment.insurance.coverage_limits")}</p>
          <ul className="divide-y divide-zinc-100">
            {e.coverage_limits.map((c, i) => (
              <li key={i} className="flex items-baseline justify-between py-2 text-sm">
                <span className="text-zinc-700">{c.label}</span>
                <span className="font-semibold text-zinc-950 tabular-nums">{c.amount}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {e.deductibles?.length > 0 && (
        <div className="mb-5" data-testid="ins-enrich-deductibles">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">{t("enrichment.insurance.deductibles")}</p>
          <ul className="divide-y divide-zinc-100">
            {e.deductibles.map((d, i) => (
              <li key={i} className="flex items-baseline justify-between py-2 text-sm">
                <span className="text-zinc-700">{d.label}</span>
                <span className="font-semibold text-zinc-950 tabular-nums">{d.amount}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {e.exclusions?.length > 0 && (
        <div data-testid="ins-enrich-exclusions">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">{t("enrichment.insurance.exclusions")}</p>
          <ul className="space-y-2">
            {e.exclusions.map((ex, i) => (
              <li key={i} className="flex gap-2 text-sm text-zinc-700 leading-relaxed">
                <span className="mt-2 w-1 h-1 rounded-full bg-rose-400 shrink-0" />
                <span>{ex}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
